import { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, Lock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import BeforeAfterSection from "@/components/BeforeAfterSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FILTER_IDS = ["all", "entreprise", "mecanique", "tolerie", "tapisserie", "formation"];

const Gallery = () => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/gallery`)
      .then((res) => {
        setItems(
          res.data.map((item) => ({
            id: item.id,
            caption: item.caption,
            category: item.category,
            src: item.url.startsWith("http") ? item.url : `${BACKEND_URL}${item.url}`,
          }))
        );
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const visible = filter === "all" ? items : items.filter((i) => i.category === filter);
  const filterLabels = t.gallery.filters;

  return (
    <main data-testid="gallery-page">
      <Seo page="gallery" />
      <section className="relative overflow-hidden pb-16 pt-40 sm:pb-20">
        <div className="pointer-events-none absolute -left-40 top-10 h-96 w-96 rounded-full bg-cream blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead as="h1" overline={t.gallery.overline} title={t.gallery.title} accent={t.gallery.titleAccent} sub={t.gallery.sub} />

          <Reveal delay={0.3}>
            <div className="mt-12 flex flex-wrap items-center gap-3">
              {FILTER_IDS.map((id) => (
                <button
                  key={id}
                  onClick={() => setFilter(id)}
                  data-testid={`gallery-filter-${id}`}
                  className={`rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all duration-300 ${
                    filter === id
                      ? "border-gold bg-gold text-white shadow-lg shadow-gold/25"
                      : "border-brand/20 bg-white text-brand hover:border-brand hover:bg-mint"
                  }`}
                >
                  {filterLabels[id]}
                </button>
              ))}
              {loaded && (
                <span className="ml-auto inline-flex items-center gap-2 rounded-full bg-mint px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand" data-testid="gallery-count">
                  <Camera className="h-3.5 w-3.5" />
                  {visible.length} {t.gallery.count}
                </span>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8">
        {loaded && visible.length === 0 && (
          <Reveal>
            <div
              className="flex flex-col items-center gap-5 rounded-3xl border-2 border-dashed border-brand/20 bg-mint/50 px-6 py-16 text-center"
              data-testid="gallery-empty-state"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-brand shadow-lg shadow-brand/10">
                <Camera className="h-7 w-7" />
              </span>
              <p className="max-w-md text-base text-forest">{t.gallery.empty}</p>
              <Link
                to="/admin"
                data-testid="gallery-admin-link"
                className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-hover"
              >
                <Lock className="h-4 w-4" />
                {t.footer.admin}
              </Link>
            </div>
          </Reveal>
        )}

        <motion.div layout className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((item, i) => (
              <motion.button
                layout
                key={item.id}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.5, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setLightbox(item)}
                data-testid={`gallery-item-${i}`}
                className="group relative aspect-[4/3] cursor-pointer overflow-hidden rounded-3xl border border-brand/10 bg-mint text-left shadow-sm transition-shadow duration-500 hover:shadow-2xl hover:shadow-brand/15"
              >
                <img
                  src={item.src}
                  alt={item.caption}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute bottom-0 left-0 right-0 translate-y-4 p-5 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                    {filterLabels[item.category] || item.category}
                  </p>
                  <p className="font-display mt-1 text-lg font-bold text-white">{item.caption}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <div className="bg-mint">
        <BeforeAfterSection />
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-forest/95 p-5 backdrop-blur-md"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-h-[85vh] max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightbox.src}
                alt={lightbox.caption}
                className="max-h-[80vh] w-full rounded-2xl border-4 border-white object-contain"
              />
              <div className="mt-4 flex items-center justify-between">
                <p className="font-display text-lg font-bold text-white">{lightbox.caption}</p>
                <button
                  onClick={() => setLightbox(null)}
                  data-testid="gallery-lightbox-close"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-white transition-colors hover:bg-gold-hover"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default Gallery;
